import { Book } from '../types/bookTypes'
import { getAdminToken } from './admin'

const API_URL = process.env.REACT_APP_API_URL

export interface FetchBooksResponse {
  success: boolean
  books?: Book[]
  message?: string
}

export interface CreateBookResponse {
  success: boolean
  book?: Book
  message?: string
}

export interface UpdateBookResponse {
  success: boolean
  book?: Book
  message?: string
}

export interface DeleteBookResponse {
  success: boolean
  book?: Book
  message?: string
}

// Helper to create auth headers for FormData requests (no Content-Type)
const createFormDataAuthHeaders = (): HeadersInit => {
  const token = getAdminToken()
  const headers: HeadersInit = {}
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

export const fetchBooks = async (): Promise<FetchBooksResponse> => {
  try {
    const token = getAdminToken()
    const headers: HeadersInit = {}
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    
    const response = await fetch(`${API_URL}/books`, { headers })
    const data = await response.json()
    if (response.ok) {
      return { success: true, books: data.books }
    } else {
      return {
        success: false,
        message: data.message || 'Failed to fetch books',
      }
    }
  } catch (error) {
    console.error('Error trying to fetch books', error)
    return {
      success: false,
      message: 'An error occurred during fetching books',
    }
  }
}

export const createBook = async (
  bookData: FormData
): Promise<CreateBookResponse> => {
  try {
    const response = await fetch(`${API_URL}/books`, {
      method: 'POST',
      headers: createFormDataAuthHeaders(),
      body: bookData,
    })
    const data = await response.json()
    if (response.ok) {
      return { success: true, book: data.book }
    } else {
      return {
        success: false,
        message: data.message || 'Failed to create book',
      }
    }
  } catch (error) {
    console.error('Error trying to create a book', error)
    return {
      success: false,
      message: 'An error occurred during book creation',
    }
  }
}

export const updateBook = async (
  bookId: string,
  bookData: FormData
): Promise<UpdateBookResponse> => {
  try {
    const response = await fetch(`${API_URL}/books/${bookId}`, {
      method: 'PUT',
      headers: createFormDataAuthHeaders(),
      body: bookData,
    })
    const data = await response.json()
    if (response.ok) {
      return { success: true, book: data.book }
    } else {
      return {
        success: false,
        message: data.message || 'Failed to update book',
      }
    }
  } catch (error) {
    console.error('Error trying to update a book', error)
    return {
      success: false,
      message: 'An error occurred during book update',
    }
  }
}

export const deleteBook = async (
  bookId: string
): Promise<DeleteBookResponse> => {
  try {
    const token = getAdminToken()
    const headers: HeadersInit = {}
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    
    const response = await fetch(`${API_URL}/books/${bookId}`, {
      method: 'DELETE',
      headers,
    })

    const data = await response.json()

    if (response.ok) {
      return { success: true, book: data.book, message: data.message }
    } else {
      return {
        success: false,
        message: data.message || 'Failed to delete book',
      }
    }
  } catch (error) {
    console.error('Error trying to delete a book', error)
    return {
      success: false,
      message: 'An error occurred during book deletion',
    }
  }
}
